import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Chef } from '../types';
import type { FacebookOwner } from './facebookDisplay';
import { finalizeChefDraft } from './chefCsv';
import {
    extractFacebookPageSlug,
    facebookPageUrl,
    normalizeFacebookPageSlug,
} from './facebookDisplay';
import { getChefWebsites, isChefActive } from './chefDisplay';

function displayNameFromFacebookSlug(slug: string): string {
    return slug
        .split(/[._-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

async function enrichStoredChef(chef: Chef, pageSlug: string, pageUrl: string): Promise<Chef> {
    const hasFacebook = getChefWebsites(chef).some((link) => extractFacebookPageSlug(link) === pageSlug);
    const websites = hasFacebook ? getChefWebsites(chef) : [...getChefWebsites(chef), pageUrl];
    const updates: Record<string, unknown> = { updatedAt: new Date().toISOString() };
    let next = chef;

    if (!hasFacebook) {
        updates.websites = websites;
        updates.website = websites[0];
        next = { ...chef, websites, website: websites[0] };
    }

    if (Object.keys(updates).length > 1) {
        await updateDoc(doc(db, 'chefs', chef.id), updates);
    }
    return next;
}

export async function resolveFacebookChef(
    owner: FacebookOwner,
    existingChefs: Chef[],
): Promise<Chef | null> {
    const pageSlug = normalizeFacebookPageSlug(owner.pageSlug);
    if (!pageSlug) return null;

    const pageUrl = owner.pageUrl?.trim() || facebookPageUrl(pageSlug);
    if (!pageUrl) return null;

    const existing = existingChefs.find(
        (chef) => isChefActive(chef) && getChefWebsites(chef).some(
            (link) => extractFacebookPageSlug(link) === pageSlug,
        ),
    );

    if (existing) {
        return enrichStoredChef(existing, pageSlug, pageUrl);
    }

    const created = finalizeChefDraft({
        name: displayNameFromFacebookSlug(pageSlug),
        englishName: pageSlug,
        websites: [pageUrl],
    });
    if (!created) return null;

    const alreadyStored = existingChefs.find((chef) => chef.id === created.id);
    if (alreadyStored) {
        return enrichStoredChef(alreadyStored, pageSlug, pageUrl);
    }

    await setDoc(doc(db, 'chefs', created.id), created);
    return created;
}
